import React, {useContext, useState} from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import {CircularProgress} from "@mui/material";
import {ToasterContext} from "../../components/shared/Toaster/ToasterContext";
import {useAuth} from "../../hooks/useAuth";
import {useMutationUpdatePharmacy} from "../../queries/pharmacy/hooks/useMutationUpdatePharmacy";
import {NotificationType, SERVER_URL} from "../../constants/global";
import { pageTitleSx } from "../../constants/designTokens";
import Typography from "@mui/material/Typography";
import LicenseUploadCard from "../../components/shared/LicenseUploadCard/LicenseUploadCard";

interface PharmacyInfoProps {
  pharmacy: any;
  statuses: any;
}

const PharmacyFiles:React.FunctionComponent<PharmacyInfoProps> = ({
  pharmacy,
  statuses,
}) => {
  const deaLicenseDefaultFile = pharmacy.PharmacyFiles.find((file: any) => file.File.type === 'deaLicense');
  const ncpdpLicenseDefaultFile = pharmacy.PharmacyFiles.find((file: any) => file.File.type === 'ncpdpLicense');
  const {addToast} = useContext(ToasterContext);
  const { token } = useAuth();
  const [deaLicenseFile, setDeaLicenseFile] = useState<any>(
    new File(
      [deaLicenseDefaultFile.File],
      deaLicenseDefaultFile.File.name,
      { type: deaLicenseDefaultFile.File.type }
      )
  );
  const [ncpdpLicenseFile, setNcpdpLicenseFile] = useState<any>(
    new File(
      [ncpdpLicenseDefaultFile.File],
      ncpdpLicenseDefaultFile.File.name,
      { type: ncpdpLicenseDefaultFile.File.type }
    )
  );
  const [uploadLoading, setUploadLoading] = useState<boolean>(false);

  const [updatePharmacy, { loading }] = useMutationUpdatePharmacy();
  const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB

  const validateFileSize = (file: File): boolean => {
    if (file.size > MAX_FILE_SIZE) {
      addToast({ 
        type: NotificationType.Error, 
        title: `File "${file.name}" is too large. Maximum size is 15 MB.` 
      });
      return false;
    }
    return true;
  };

  const handleDeaLicenseFileChange = (file: File) => {
    if (!validateFileSize(file)) return;

    const newFile = new File([file], `${Date.now()}_${file.name}`, {
      type: file.type,
    });

    setDeaLicenseFile(newFile);
  }
  const handleNcpdpLicenseFileChange = (file: File) => {
    if (!validateFileSize(file)) return;

    const newFile = new File([file], `${Date.now()}_${file.name}`, {
      type: file.type,
    });

    setNcpdpLicenseFile(newFile);
  }

  const handleSave = async () => {
    let pharmacyDeaLicenseFileId: number = 0;
    let pharmacyNcpdpLicenseFileId : number = 0;

    pharmacy.PharmacyFiles.forEach((pharmacyFile: any) => {
      if (pharmacyFile.File.type === 'deaLicense') {
        pharmacyDeaLicenseFileId = pharmacyFile.id;
      }
      if (pharmacyFile.File.type === 'ncpdpLicense') {
        pharmacyNcpdpLicenseFileId = pharmacyFile.id;
      }
    });

    try {
      setUploadLoading(true);
      const deaLicenseFileData = new FormData();
      deaLicenseFileData.append('file', deaLicenseFile);
      deaLicenseFileData.append('fileType', 'deaLicense');

      const ncpdpLicenseFileData = new FormData();
      ncpdpLicenseFileData.append('file', ncpdpLicenseFile);
      ncpdpLicenseFileData.append('fileType', 'ncpdpLicense');

      const deaLicensePromise: any = await fetch(`${SERVER_URL}/file/upload`, {
        method: 'POST',
        body: deaLicenseFileData,
        headers: {'Authorization': `Bearer ${token}`}
      });

      const ncpdpLicensePromise: any = await fetch(`${SERVER_URL}/file/upload`, {
        method: 'POST',
        body: ncpdpLicenseFileData,
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (deaLicensePromise.ok && ncpdpLicensePromise.ok) {
        setUploadLoading(true);
        const deaLicenseDataRes = await deaLicensePromise.json();
        const ncpdpLicenseDataRes = await ncpdpLicensePromise.json();

        const deaLicenseRequestData = {
          PharmacyFiles: {
            update: {
              data: {
                File: {
                  connect: {
                    id: deaLicenseDataRes.id
                  }
                }
              },
              where: {
                id: pharmacyDeaLicenseFileId
              }
            }
          },
          PharmacyStatus: {
            update: {
              Status: {
                connect: {
                  id: statuses.find((status: any) => status.key === 'LICENSE_UPLOADED').id
                }
              },
            }
          },
        }

        const ncpdpLicenseRequestData = {
          PharmacyFiles: {
            update: {
              data: {
                File: {
                  connect: {
                    id: ncpdpLicenseDataRes.id
                  }
                }
              },
              where: {
                id: pharmacyNcpdpLicenseFileId
              }
            }
          },
          PharmacyStatus: {
            update: {
              Status: {
                connect: {
                  id: statuses.find((status: any) => status.key === 'LICENSE_UPLOADED').id
                }
              },
            }
          },
        }

        updatePharmacy({ variables: { data: deaLicenseRequestData, where: { id: pharmacy.id } } }).then(() => {
          updatePharmacy({ variables: { data: ncpdpLicenseRequestData, where: { id: pharmacy.id } } }).then(() => {
            window.location.reload();
          }).catch((error: any) => {
            addToast({ type: NotificationType.Error, title: error.message  });
          });
        }).catch((error: any) => {
          addToast({ type: NotificationType.Error, title: error.message  });
        });
      } else {
        setUploadLoading(false);
      }
    } catch (error: any) {
      setUploadLoading(false);
      addToast({ type: NotificationType.Error, title: error.message  });
    }
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography component="h1" sx={{ ...pageTitleSx, mb: 3 }}>Pharmacy Licenses</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: { xs: 'center', md: 'flex-start' } }}>
        <LicenseUploadCard
          title="Upload DEA License"
          fileName={deaLicenseFile?.name}
          fileUrl={deaLicenseDefaultFile?.File?.key ? `${SERVER_URL}/file/${deaLicenseDefaultFile.File.key}` : undefined}
          onFileSelect={handleDeaLicenseFileChange}
        />
        <LicenseUploadCard
          title="Upload Pharmacy License"
          fileName={ncpdpLicenseFile?.name}
          fileUrl={ncpdpLicenseDefaultFile?.File?.key ? `${SERVER_URL}/file/${ncpdpLicenseDefaultFile.File.key}` : undefined}
          onFileSelect={handleNcpdpLicenseFileChange}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' }, mt: 4 }}>
        <Button size='large' variant="contained" sx={{width: '200px'}} onClick={handleSave}>
          Save
          {(loading || uploadLoading) && <CircularProgress sx={{ml: 2}} size={14} color='inherit' />}
        </Button>
      </Box>
    </Box>
  )
}

export default PharmacyFiles;
